import { CommonModule } from "@angular/common";
import { Component, computed, DestroyRef, inject, input, OnInit, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { MatInputModule } from "@angular/material/input";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MAT_DATE_FORMATS, MatNativeDateModule } from "@angular/material/core";
import { MatRadioModule } from "@angular/material/radio";
import { MatOption, MatSelect } from "@angular/material/select";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { FileSaverService } from "ngx-filesaver";
import { first, map, take } from "rxjs";
import {
  LorenNumericLineChartReportComponent
} from "../../../loren-assets-reports/components/loren-numeric-line-chart-report/loren-numeric-line-chart-report.component";
import {
  LorenTrackingReportComponent
} from "../../../loren-assets-reports/components/loren-tracking-report/loren-tracking-report.component";
import {
  AssetImageHistoryPlayerComponent
} from "../../../photo-player/components/asset-image-history-player/asset-image-history-player.component";
import {
  LorenDownloadPdfService
} from "../../../loren-assets-reports/services/loren-download-pdf-service/loren-download-pdf.service";
import {
  LorenReportsService
} from "../../../loren-assets-reports/services/loren-reports-service/loren-reports.service";
import {
  LorenReportsUtilsService
} from "../../../loren-assets-reports/services/loren-reports-utils-service/loren-reports-utils.service";
import { LorenAsset } from "../../../loren-assets/interfaces/loren-asset.interface";
import { LorenMeteringType } from "../../../loren-assets/interfaces/loren-metering-type";
import { LorenAggregate } from "../../../../shared/enums/loren-aggregate.enum";
import { LorenFeature } from "../../../../shared/enums/loren-feature.enum";
import {
  LorenMetricCategories,
  LorenMetricKinds,
} from "../../../../shared/modules/loren-asset-card/constants/loren-metric-types.constants";
import {
  LorenFeatureToggleService
} from "../../../../shared/services/loren-feature-toggle-service/loren-feature-toggle.service";
import { LorenContentTypes } from "../../../../shared/services/loren-http-service/enums/loren-content-types.enums";
import {
  LorenAssetDetailsChartsDialogComponent
} from "../loren-asset-details-charts-dialog/loren-asset-details-charts-dialog.component";
import { LorenSnackBarService } from "../../../../shared/services/loren-snack-bar-service/loren-snack-bar.service";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { LorenMetering } from "../../../loren-assets/interfaces/loren-metering.interface";
import {
  DateRange,
  evaluateDateRange,
  PeriodSelectComponent
} from "../../../../common/forms/period-select/period-select.component";
import { Duration, Instant } from "@js-joda/core";
import { RefreshSubject } from "../../../../common/rxjs/RefreshSubject";
import { MatButton, MatIconButton } from "@angular/material/button";
import {SelectAllDirective} from "../../../../common/forms/select-all.directive";

const NATIVE_DATE_FORMATS = {
  parse: {
    dateInput: "LL",
  },
  display: {
    dateInput: "dd MMMM yyyy",
    monthYearLabel: "MMM yyyy",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM yyyy",
  },
};

@Component({
  selector: "loren-loren-asset-details-charts",
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelect,
    MatOption,
    MatIcon,
    MatTooltip,
    MatRadioModule,
    MatProgressSpinner,
    TranslocoPipe,
    PeriodSelectComponent,
    LorenNumericLineChartReportComponent,
    LorenTrackingReportComponent,
    AssetImageHistoryPlayerComponent,
    ReactiveFormsModule,
    MatIconButton,
    MatButton,
    SelectAllDirective,
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: NATIVE_DATE_FORMATS },
  ],
  templateUrl: "./loren-asset-details-charts.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./loren-asset-details-charts.component.scss",
})
export class LorenAssetDetailsChartsComponent implements OnInit {
  private dialog = inject(MatDialog);
  private transloco = inject(TranslocoService);
  private fileSaverService = inject(FileSaverService);
  private featureToggleService = inject(LorenFeatureToggleService);
  private reportsService = inject(LorenReportsService);
  private downloadPDFService = inject(LorenDownloadPdfService);
  private snackbarService = inject(LorenSnackBarService);
  private router = inject(Router);
  protected readonly refresh$ = new RefreshSubject();

  private readonly destroyRef = inject(DestroyRef);
  asset = input.required<LorenAsset>();
  isHandset = input<boolean>();

  readonly categories = LorenMetricCategories;

  readonly fileExtensionOptions = signal([
    LorenContentTypes.EXCEL,
    LorenContentTypes.PDF,
  ]);

  private pdfIsSupported = signal<boolean>(false);

  downloadLoading = signal<boolean>(false);
  readonly periodControl = new FormControl<DateRange>(Duration.ofHours(24), {
    nonNullable: true,
  });
  readonly period = toSignal(this.periodControl.valueChanges, {
    initialValue: this.periodControl.value,
  });

  readonly selectedMetricsControl = new FormControl<number[]>([], {
    nonNullable: true,
  });
  readonly selectedMetrics = toSignal(this.selectedMetricsControl.valueChanges, {
    initialValue: this.selectedMetricsControl.value,
  });
  selectedFileExtension = signal<LorenContentTypes>(LorenContentTypes.EXCEL);
  selectedAggregation = signal<LorenAggregate>(null);
  meteringsHaveData = signal<Record<string, boolean>>({});

  availableMeterings = computed(() =>
    this.asset().metering.filter(
      (m) => m.type !== LorenMeteringType.FILL_LEVEL_SENSOR
    )
  );

  meterings = computed(
    () =>
      new Set(
        this.availableMeterings().filter((m) => this.selectedMetrics().includes(m.id))
          .map((m) => m.type)
      )
  );

  hasGpsTracking = computed(() => {
    const currentUrl = this.router.url;
    const isDashboardRoute = currentUrl === '/dashboard' || currentUrl === '/' || currentUrl === '';

    if (!isDashboardRoute) {
      return false;
    }

    return this.meterings().has(LorenMeteringType.GPS_TRACKER_SENSOR);
  });

  allMeteringIds = computed(() => this.availableMeterings().map((m) => m.id));

  pdfDownloadOption = computed(
    () =>
      this.pdfIsSupported() &&
      (this.meterings().has(LorenMeteringType.HUMIDITY_SENSOR) ||
        this.meterings().has(LorenMeteringType.TEMPERATURE_SENSOR))
  );

  meteringType = computed(() => {
    const types: Partial<Record<LorenMeteringType, LorenMetering[]>> = {};
    for (const m of this.availableMeterings()) {
      if (!this.selectedMetrics().includes(m.id)) {
        continue;
      }

      if (types[m.type] === undefined) {
        types[m.type] = [];
      }

      types[m.type].push(m);
    }

    return types;
  })

  ngOnInit() {
    const navigation = this.router.currentNavigation();
    if (navigation?.extras?.state) {
      const { selectedMeteringId } = navigation.extras.state;

      if (selectedMeteringId) {
        this.selectedMetricsControl.setValue(
          this.allMeteringIds().includes(selectedMeteringId)
            ? [selectedMeteringId]
            : []
        );
      } else {
        this.selectedMetricsControl.setValue(this.allMeteringIds());
      }
    } else {
      this.selectedMetricsControl.setValue(this.allMeteringIds());
    }

    this.featureToggleService
      .userHasFeature(LorenFeature.GENERATE_PDF_REPORT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => this.pdfIsSupported.set(res));
  }

  getMeteringsForType(type: LorenMeteringType) {
    return this.meteringType()[type] ?? [];
  }

  getMeteringKind(type: LorenMeteringType) {
    return LorenMetricKinds.get(type);
  }

  updateSelectedFileExtension(event) {
    this.selectedFileExtension.set(event.value);
  }

  handleDownloadButtonClicked() {
    if (this.selectedFileExtension() === LorenContentTypes.EXCEL) {
      if (
        Array.from(this.meterings()).find(
          (m) => this.getMeteringKind(m) === LorenMetricCategories.NUMERIC
        )
      ) {
        const dialog = this.getDialog();

        dialog
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((result) => {
            if (!result) {
              return;
            }
            this.selectedAggregation.set(result);
            this.handleExportExcel();
          });
      } else {
        this.handleExportExcel();
      }
    } else {
      this.downloadPDF();
    }
  }

  handleExportExcel() {
    // TODO this should be refactor and select only the meterings type that are selected
    // If we decide to export meterings from different types with one click, this should be extended
    // requirements are not clear

    const numericMeteringIds = [
        ...this.getMeteringsForKind(LorenMetricCategories.NUMERIC),
        ...this.getMeteringsForKind(LorenMetricCategories.PERCENTAGE),
    ];

    const trackingMeteringIds = this.getMeteringsForKind(
      LorenMetricCategories.TRACKING
    );

    const binaryMeteringIds = this.getMeteringsForKind(
      LorenMetricCategories.BINARY
    );

    const presenceMeteringIds = this.getMeteringsForKind(
      LorenMetricCategories.PRESENCE_SENSOR
    );

    const [from, to] = evaluateDateRange(this.period());

    if (numericMeteringIds.length > 0) {
      this.downloadXLS(
        LorenMetricCategories.NUMERIC,
        numericMeteringIds,
        from,
        to,
        this.selectedAggregation()
      );
    }

    if (trackingMeteringIds.length > 0) {
      // TODO implement tracking export
      // this.reportsService.exportSoilData(trackingMeteringIds, fromDate, toDate, aggregate).pipe(take(1)).subscribe(res => {
      //     const blob = new Blob([res], {type: LorenContentTypes.EXCEL});
      //     const filename = LorenReportsUtilsService.getExportFilename(this.selectedAssets, this.selectedPeriod);
      //     this.fileSaverService.save(blob, filename);
      // });
    }

    if (binaryMeteringIds.length > 0) {
      this.downloadXLS(
        LorenMetricCategories.BINARY,
        binaryMeteringIds,
        from,
        to
      );
    }

    if (presenceMeteringIds.length > 0) {
      this.downloadXLS(
        LorenMetricCategories.PRESENCE_BEACON,
        presenceMeteringIds,
        from,
        to
      );
    }
  }

  downloadPDF() {
    const [from, to] = evaluateDateRange(this.period());
    const numOfMetrics = new Set(
      this.selectedMetrics()
        .map((m) => this.asset().metering.find((met) => met.id === m))
        .filter((m) => this.isTypeEligibleForPdf(m.type))
        .map((x) => x.type)
    ).size;
    this.downloadPDFService.setNumOfReports(numOfMetrics);
    this.downloadPDFService.triggerImageSelection();

    this.getPDF(from, to);
  }

  chartUpdatedHandler(event) {
    if (!event || !Array.isArray(event)) {
      return;
    }

    const chartsHaveData = event.map((e) => ({
      id: e.id,
      hasData: !!e.series?.length,
    }));

    chartsHaveData.forEach((e) =>
      this.meteringsHaveData.update((v) => ({ ...v, [e.id]: e.hasData }))
    );
  }

  checkMeteringsHaveData() {
    return Object.keys(this.meteringsHaveData()).some(
      (m) => this.meteringsHaveData()[m]
    );
  }


  private getMeteringsForKind(kind: LorenMetricCategories) {
    return this.selectedMetrics()
      .filter(
        (m) =>
          LorenMetricKinds.get(
            this.asset().metering.find((met) => met.id === m).type
          ) === kind
      )
      .map((m) => m.toString());
  }

  private isTypeEligibleForPdf(metering: LorenMeteringType) {
    return (
      metering === LorenMeteringType.HUMIDITY_SENSOR ||
      metering === LorenMeteringType.TEMPERATURE_SENSOR
    );
  }

  private getPDF(fromDate: Instant, toDate: Instant) {

    this.downloadPDFService.payloadReady
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        map((x) => {
          return x.map((y) => ({
            meteringIds: y.ids,
            base64MeteringGraphic: y.svg.imgURI,
          }));
        })
      )
      .subscribe((x) => {
        this.downloadLoading.set(true);
        this.reportsService.exportDataInPDF(x, fromDate, toDate)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (res) => {
              this.downloadLoading.set(false);
              const blob = new Blob([res], {
                type: LorenContentTypes.PDF,
              });
              const filename = LorenReportsUtilsService.getExportFilename(
                [this.asset()],
                this.period(),
                LorenContentTypes.PDF
              );
              this.fileSaverService.save(blob, filename);
            },
            error: () => {
              this.downloadLoading.set(false);
              this.snackbarService.showErrorMessage(
                this.transloco.translate("error_page.general_error_message")
              );
            },
          });
      });
  }

  private downloadXLS(
    type: LorenMetricCategories,
    meteringIds: string[],
    fromDate: Instant,
    toDate: Instant,
    aggregate: LorenAggregate = null
  ) {
    this.downloadLoading.set(true);
    this.reportsService
      .exportDataInXLS(type, meteringIds, fromDate, toDate, aggregate)
      .pipe(takeUntilDestroyed(this.destroyRef), first())
      .subscribe({
        next: (res) => {
          this.downloadLoading.set(false);
          const blob = new Blob([res], {
            type: LorenContentTypes.EXCEL,
          });
          const filename = LorenReportsUtilsService.getExportFilename(
            [this.asset()],
            this.period()
          );
          this.fileSaverService.save(blob, filename);
        },
        error: () => {
          this.downloadLoading.set(false);
          this.snackbarService.showErrorMessage(
            this.transloco.translate("error_page.general_error_message")
          );
        },
      });
  }

  private getDialog() {
    const dialogRef = this.dialog.open(LorenAssetDetailsChartsDialogComponent, {
      width: "50%",
    });
    return dialogRef.afterClosed();
  }
}
